import { useEffect, useState } from "react";
import { Button, type ButtonProps } from "../Button/Button";
import { VisuallyHidden } from "../VisuallyHidden/VisuallyHidden";

export interface CopyButtonProps extends Omit<ButtonProps, "onClick" | "icon" | "children"> {
  /** The text to put on the clipboard. */
  value: string;
  label?: string;
  copiedLabel?: string;
}

/** Copies text to the clipboard and confirms inline ("Copied"), announced politely. */
export function CopyButton({ value, label = "Copy", copiedLabel = "Copied", ...rest }: CopyButtonProps) {
  const [state, setState] = useState<"idle" | "copied" | "failed">("idle");

  useEffect(() => {
    if (state === "idle") return undefined;
    const timer = window.setTimeout(() => setState("idle"), 2000);
    return () => window.clearTimeout(timer);
  }, [state]);

  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setState("copied");
    } catch {
      setState("failed");
    }
  }

  return (
    <>
      <Button {...rest} icon={state === "copied" ? "check" : "copy"} onClick={copy}>
        {state === "copied" ? copiedLabel : state === "failed" ? "Copy failed; select the text instead" : label}
      </Button>
      <VisuallyHidden>
        <span aria-live="polite">{state === "copied" ? copiedLabel : ""}</span>
      </VisuallyHidden>
    </>
  );
}
